import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from '../../../../firebase';
import { useAuth } from '../../../auth/components/AuthProvider';
import { SOLICITUDES_EMPRESA_STATUS_LABELS } from '../../../../constants/adminStatuses';

const TZ_CHILE = 'America/Santiago';

function normEmail(s) {
  return String(s || '').trim().toLowerCase();
}

function toJsDate(val) {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val.seconds != null) return new Date(val.seconds * 1000);
  if (val instanceof Date) return val;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Primera fecha de activación conocida (admin / agente / genérica). */
function fechaActivacionEmpresa(data) {
  return (
    toJsDate(data.fecha_activacion) ||
    toJsDate(data.fecha_activacion_admin) ||
    toJsDate(data.fecha_activacion_agente) ||
    toJsDate(data.fecha_credenciales_asignadas) ||
    null
  );
}

function ymdSantiago(d) {
  if (!d || Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ_CHILE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);
}

/** Criterio alineado al directorio público: perfil operativo en plataforma. */
function empresaCuentaComoActivada(data) {
  const e = String(data?.estado || '').toLowerCase();
  return e === 'activa' || e === 'validada';
}

/** Campos habituales y legacy en usuarios/{uid}. */
function emailDesdeDocUsuario(u) {
  const raw =
    u.email ||
    u.correo ||
    u.mail ||
    u.userEmail ||
    u.correoElectronico ||
    u.Email ||
    (u.usuario && (u.usuario.email || u.usuario.correo));
  const s = String(raw || '').trim();
  return s;
}

function uniqJoin(arr) {
  const u = [...new Set(arr.filter(Boolean))];
  return u.length ? u.join(' · ') : '—';
}

function nombreEmpresa(emp) {
  const n =
    emp?.nombre_empresa ||
    emp?.nombreEmpresa ||
    emp?.nombre ||
    emp?.nombreFantasia ||
    '';
  return String(n).trim() || 'Sin nombre';
}

function labelEstadoEmpresa(estado) {
  const e = String(estado || '').toLowerCase();
  return SOLICITUDES_EMPRESA_STATUS_LABELS[e] || estado || '—';
}

function formatFechaHoraChile(d) {
  if (!d || Number.isNaN(d.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', {
      timeZone: TZ_CHILE,
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return d.toLocaleString('es-CL');
  }
}

export default function AdminAdministradores() {
  const { rol, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filas, setFilas] = useState([]);
  const [avisoAuth, setAvisoAuth] = useState(null);
  /** Admin seleccionado para ver empresas activadas (`activado_por` = su correo), orden por fecha de activación. */
  const [vistaActivadas, setVistaActivadas] = useState(null);

  const esAdmin = String(rol || '').toLowerCase() === 'admin';

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAvisoAuth(null);
    setVistaActivadas(null);
    try {
      const rolesAdmin = ['admin', 'Admin', 'ADMIN'];
      const adminsQ = query(collection(db, 'usuarios'), where('rol', 'in', rolesAdmin));
      const [adminsSnap, empresasSnap] = await Promise.all([
        getDocs(adminsQ),
        getDocs(collection(db, 'empresas'))
      ]);

      const admins = adminsSnap.docs.map((d) => ({
        id: d.id,
        ...d.data()
      }));

      const empresas = empresasSnap.docs.map((d) => ({
        id: d.id,
        ...d.data()
      }));

      /** uid → email y nombre desde Auth (callable). */
      const porUidAuth = new Map();
      try {
        const fn = httpsCallable(getFunctions(app), 'listAdminUsersForDirectory');
        const { data } = await fn({});
        for (const row of data?.admins || []) {
          if (row?.uid) {
            porUidAuth.set(row.uid, {
              email: row.email ? String(row.email).trim() : '',
              nombreAuth: row.nombre ? String(row.nombre).trim() : ''
            });
          }
        }
      } catch (callErr) {
        console.warn('AdminAdministradores: listAdminUsersForDirectory', callErr);
        setAvisoAuth(
          'No se pudo obtener el correo desde Firebase Auth (¿función listAdminUsersForDirectory desplegada?). ' +
            'Los totales pueden quedar en 0 si el correo no está guardado en Firestore.'
        );
      }

      const hoyYmd = ymdSantiago(new Date());

      const activasPorAdmin = new Map();
      for (const emp of empresas) {
        const keyAct = normEmail(emp.activado_por);
        if (keyAct && empresaCuentaComoActivada(emp)) {
          if (!activasPorAdmin.has(keyAct)) activasPorAdmin.set(keyAct, []);
          activasPorAdmin.get(keyAct).push(emp);
        }
      }

      const filasBrutas = admins.map((u) => {
        const authInfo = porUidAuth.get(u.id);
        const emailRaw =
          (authInfo?.email && String(authInfo.email).trim()) ||
          emailDesdeDocUsuario(u) ||
          '';
        const k = normEmail(emailRaw);
        const lista = k ? activasPorAdmin.get(k) || [] : [];
        const activadasOrdenadas = [...lista].sort((a, b) => {
          const ta = fechaActivacionEmpresa(a)?.getTime() ?? 0;
          const tb = fechaActivacionEmpresa(b)?.getTime() ?? 0;
          if (tb !== ta) return tb - ta;
          return nombreEmpresa(a).localeCompare(nombreEmpresa(b), 'es');
        });
        let activadasHoy = 0;
        for (const emp of lista) {
          const fa = fechaActivacionEmpresa(emp);
          if (fa && ymdSantiago(fa) === hoyYmd) activadasHoy += 1;
        }
        const nombre =
          u.nombre ||
          u.displayName ||
          u.nombres ||
          authInfo?.nombreAuth ||
          '—';
        const emailDisplay =
          emailRaw || `Sin correo en Auth (UID ${u.id.slice(0, 8)}…)`;
        return {
          id: u.id,
          nombre,
          emailRaw,
          emailNorm: k,
          emailDisplay,
          empresasActivadasTotal: lista.length,
          empresasActivadasHoy: activadasHoy,
          empresasActivadasLista: activadasOrdenadas
        };
      });

      /** Una fila por correo: evita duplicar el mismo admin con dos documentos en usuarios. */
      const porClave = new Map();
      for (const row of filasBrutas) {
        const clave = row.emailNorm || `uid:${row.id}`;
        if (!porClave.has(clave)) {
          porClave.set(clave, {
            id: row.id,
            ids: [row.id],
            nombres: [row.nombre],
            emailDisplay: row.emailDisplay,
            emailNorm: row.emailNorm,
            empresasActivadasTotal: row.empresasActivadasTotal,
            empresasActivadasHoy: row.empresasActivadasHoy,
            empresasActivadasLista: row.empresasActivadasLista
          });
        } else {
          const agg = porClave.get(clave);
          agg.ids.push(row.id);
          agg.nombres.push(row.nombre);
        }
      }

      const filasCalc = [...porClave.values()]
        .map((agg) => ({
          id: agg.id,
          nombre: uniqJoin(agg.nombres),
          email: agg.emailDisplay,
          empresasActivadasTotal: agg.empresasActivadasTotal,
          empresasActivadasHoy: agg.empresasActivadasHoy,
          empresasActivadasLista: agg.empresasActivadasLista || [],
          cuentasFirestore: agg.ids.length
        }))
        .sort((a, b) => {
          const ea = a.email.includes('Sin correo') ? `zzz${a.email}` : a.email;
          const eb = b.email.includes('Sin correo') ? `zzz${b.email}` : b.email;
          return ea.localeCompare(eb, 'es');
        });

      setFilas(filasCalc);
    } catch (e) {
      console.error('AdminAdministradores:', e);
      setError(e?.message || 'Error al cargar datos');
      setFilas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && esAdmin) cargar();
  }, [authLoading, esAdmin, cargar]);

  const totales = useMemo(() => {
    let totalActivadas = 0;
    let hoy = 0;
    for (const f of filas) {
      totalActivadas += f.empresasActivadasTotal;
      hoy += f.empresasActivadasHoy;
    }
    return { totalActivadas, hoy, cantidadAdmins: filas.length };
  }, [filas]);

  if (authLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center p-8 text-slate-600">
        Cargando sesión…
      </div>
    );
  }

  if (!esAdmin) {
    return (
      <div className="p-8">
        <p className="text-red-600 font-medium">Solo administradores pueden ver esta página.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Administradores</h1>
        <p className="text-gray-600 text-sm mt-1">
          Usuarios con rol admin. Las empresas en estado <span className="font-medium">activa</span> o{' '}
          <span className="font-medium">validada</span> con <code className="text-xs bg-gray-100 px-1 rounded">activado_por</code>{' '}
          igual al correo cuentan en las columnas de activadas. El botón abre el listado de esas mismas empresas, ordenado por
          fecha/hora de activación. El correo se toma de <span className="font-medium">Firebase Auth</span> (y de Firestore si
          existe). &quot;Hoy&quot; usa zona horaria Chile (America/Santiago).
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-slate-900">{totales.cantidadAdmins}</div>
          <div className="text-sm text-slate-600">Administradores</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-emerald-900">{totales.totalActivadas}</div>
          <div className="text-sm text-emerald-800">Empresas activadas (total atribuido)</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-amber-900">{totales.hoy}</div>
          <div className="text-sm text-amber-800">Activaciones hoy (Chile)</div>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 text-red-800 px-4 py-3 text-sm">{error}</div>
      )}

      {avisoAuth && (
        <div className="mb-4 rounded-md bg-amber-50 text-amber-900 px-4 py-3 text-sm border border-amber-200">
          {avisoAuth}
        </div>
      )}

      {loading ? (
        <p className="text-gray-600">Cargando administradores…</p>
      ) : filas.length === 0 ? (
        <p className="text-gray-600">No se encontraron usuarios con rol administrador.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left">
                <th className="px-4 py-3 font-semibold text-gray-700">Nombre</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Correo</th>
                <th className="px-4 py-3 font-semibold text-gray-700 text-right">Empresas activadas (total)</th>
                <th className="px-4 py-3 font-semibold text-gray-700 text-right">Hoy</th>
                <th className="px-4 py-3 font-semibold text-gray-700 text-left">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50/80">
                  <td className="px-4 py-3 text-gray-900">
                    <div>{f.nombre}</div>
                    {f.cuentasFirestore > 1 && (
                      <div className="text-xs text-amber-700 mt-0.5">
                        {f.cuentasFirestore} documentos en <code className="bg-amber-100 px-1 rounded">usuarios</code> con el
                        mismo correo
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 break-all">{f.email}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-900">
                    {f.empresasActivadasTotal}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-800">{f.empresasActivadasHoy}</td>
                  <td className="px-4 py-3 align-middle">
                    <button
                      type="button"
                      onClick={() => {
                        if (vistaActivadas?.id === f.id) {
                          setVistaActivadas(null);
                          return;
                        }
                        setVistaActivadas({
                          id: f.id,
                          nombre: f.nombre,
                          email: f.email,
                          empresas: f.empresasActivadasLista || [],
                        });
                      }}
                      disabled={(f.empresasActivadasLista?.length ?? 0) === 0}
                      className={`inline-flex items-center rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                        (f.empresasActivadasLista?.length ?? 0) === 0
                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                          : vistaActivadas?.id === f.id
                            ? 'bg-blue-100 text-blue-900 ring-2 ring-blue-300'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {(f.empresasActivadasLista?.length ?? 0) === 0
                        ? 'Sin empresas activadas'
                        : vistaActivadas?.id === f.id
                          ? 'Ocultar listado'
                          : `Ver empresas activadas (${f.empresasActivadasLista.length})`}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filas.length > 0 && vistaActivadas && vistaActivadas.empresas.length > 0 && (
        <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50/90 p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Empresas activadas por este administrador</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                <span className="font-medium text-slate-800">{vistaActivadas.nombre}</span>
                <span className="text-slate-400 mx-2">·</span>
                <span className="break-all">{vistaActivadas.email}</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Misma atribución que la columna &quot;activadas&quot;:{' '}
                <code className="bg-white px-1 rounded border">activado_por</code> = correo del admin. Orden por fecha de
                activación (
                <code className="bg-white px-1 rounded border">fecha_activacion</code>,{' '}
                <code className="bg-white px-1 rounded border">fecha_activacion_admin</code>, etc.). Zona horaria: Chile.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setVistaActivadas(null)}
              className="shrink-0 text-sm font-semibold text-slate-600 hover:text-slate-900 underline"
            >
              Cerrar listado
            </button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-left text-slate-700">
                  <th className="px-3 py-2 font-semibold">Activación</th>
                  <th className="px-3 py-2 font-semibold">Empresa</th>
                  <th className="px-3 py-2 font-semibold">Estado</th>
                  <th className="px-3 py-2 font-semibold text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {vistaActivadas.empresas.map((emp) => (
                  <tr key={emp.id} className="border-t border-slate-100 hover:bg-slate-50/80">
                    <td className="px-3 py-2 text-gray-800 tabular-nums whitespace-nowrap">
                      {formatFechaHoraChile(fechaActivacionEmpresa(emp))}
                    </td>
                    <td className="px-3 py-2 text-gray-900">{nombreEmpresa(emp)}</td>
                    <td className="px-3 py-2 text-gray-700">{labelEstadoEmpresa(emp.estado)}</td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/admin/editar-empresa/${emp.id}`}
                        className="font-semibold text-blue-600 hover:text-blue-800"
                      >
                        Editar empresa →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && filas.length > 0 && (
        <p className="mt-4 text-xs text-gray-500">
          Las activaciones hechas por agentes (p. ej. <code className="bg-gray-100 px-1 rounded">activado_por: agente</code>) no se atribuyen a un administrador.
        </p>
      )}
    </div>
  );
}
